import type { ReactNode } from "react";
import { Drawer, DrawerContent } from "../components/arc/drawer/drawer";
import { Timeline } from "../components/arc/timeline/timeline";
import styles from "./record.module.css";

/**
 * A record beside its list (Arc: "a long form or record detail beside the page"). Pages also
 * give each record its own route, so a link opens it straight away.
 */
export function RecordDrawer({
  open,
  onOpenChange,
  title,
  description,
  children,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  children: ReactNode;
}) {
  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent side="right" title={title} description={description} className={styles.drawer}>
        <div className={styles.body}>{children}</div>
      </DrawerContent>
    </Drawer>
  );
}

/** A record's facts as label and value pairs. An empty value reads as a dash. */
export function DetailList({ items }: { items: { label: string; value: ReactNode }[] }) {
  return (
    <dl className={styles.details}>
      {items.map((item) => (
        <div key={item.label} className={styles.detail}>
          <dt>{item.label}</dt>
          <dd>{item.value === null || item.value === undefined || item.value === "" ? "—" : item.value}</dd>
        </div>
      ))}
    </dl>
  );
}

export interface ActivityEntry {
  id: string;
  verb: string;
  target_type: string;
  created_at: string;
  diff?: Record<string, { from?: unknown; to?: unknown }> | null;
  payload?: Record<string, unknown> | null;
}

const show = (value: unknown) =>
  value === null || value === undefined ? "nothing" : typeof value === "object" ? JSON.stringify(value) : String(value);

/** An audit entry's changes, field by field: "status: active → inactive". */
export function ChangeList({ diff }: { diff: ActivityEntry["diff"] }) {
  const changes = Object.entries(diff ?? {});
  if (changes.length === 0) return null;
  return (
    <ul className={styles.changes}>
      {changes.map(([field, change]) => (
        <li key={field}>
          <span className={styles.field}>{field.replace(/_/g, " ")}</span>: {show(change.from)} → {show(change.to)}
        </li>
      ))}
    </ul>
  );
}

/** What happened to a record, newest first (the audit trail, `GET /v1/admin/audit`). */
export function ActivityTimeline({
  entries,
  timeZone,
  now = Date.now(),
  label = "Activity",
}: {
  entries: ActivityEntry[];
  timeZone: string;
  now?: number;
  label?: string;
}) {
  return (
    <Timeline
      label={label}
      now={now}
      timeZone={timeZone}
      events={entries.map((e) => {
        const changes = Object.entries(e.diff ?? {});
        // why it was done, where the person gave a reason (a partner suspended, an order cancelled)
        const reason = typeof e.payload?.reason === "string" ? e.payload.reason : null;
        return {
          id: e.id,
          at: e.created_at,
          title: `${e.verb[0]?.toUpperCase() ?? ""}${e.verb.slice(1).replace(/_/g, " ")}`,
          // the reason shows on the row; the fields it changed show when it's opened
          meta: reason
            ? `Reason: ${reason}`
            : changes.length
              ? changes.map(([field]) => field.replace(/_/g, " ")).join(", ")
              : undefined,
          detail: changes.length ? <ChangeList diff={e.diff} /> : undefined,
        };
      })}
    />
  );
}
